import React, { useMemo, useState } from "react";
import { Search, BookOpen, TerminalSquare } from "lucide-react";

import { mock } from "@/mock";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { ScrollArea } from "@/components/ui/scroll-area";

const makeOutput = (configText) => {
  // intentionally “simulated”: pull a few hints from an HCL-ish mock definition
  const pick = (re, fallback = null) => {
    const m = configText.match(re);
    return m ? String(m[1]).trim() : fallback;
  };

  const service = pick(/service\s+"([^"]+)"/m, "service");
  const route = pick(/\broute\s*=\s*"([^"]+)"/m, "/");
  const method = pick(/\bmethod\s*=\s*"([^"]+)"/m, "GET").toUpperCase();
  const status = Number(pick(/\bstatus\s*=\s*(\d{3})/m, "200"));

  const now = new Date();
  const hhmm = now
    .toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    .replace(/\s/g, "");

  return {
    summary: `Run summary (simulated)\n\nproject: smaughk\nenv: demo-${service}\nstarted: ${hhmm}\n\nmock services:\n  - ${service}\n\nroutes:\n  - ${method} ${route}  →  ${status}\n\nsynthetic environment:\n  status: ready\n  policy: zero-trust overlay\n  tunnels: 0 public (disabled)\n\nnotes:\n  This output is a placeholder while the interactive tool is under development.\n`,
    hints: [
      "Keep mocks in code — version them like infrastructure.",
      "Mix real + synthetic responses inside a Synthetic Environment™.",
      "Share environments with your team without opening inbound ports.",
    ],
  };
};

export default function Docs() {
  const tree = useMemo(() => mock.docs.tree, []);
  const pages = mock.docs.pages;

  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState("intro");
  const [config, setConfig] = useState(mock.docs.starterConfig);

  const flatItems = useMemo(() => {
    const out = [];
    for (const g of tree) {
      for (const it of g.items) out.push({ ...it, group: g.group });
    }
    return out;
  }, [tree]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tree;
    return tree
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            it.title.toLowerCase().includes(q) || g.group.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [query, tree]);

  const active = pages[activeId] || pages.intro;
  const output = useMemo(() => makeOutput(config), [config]);

  return (
    <div className="pb-16">
      <section className="mx-auto max-w-6xl px-6 pt-10 sm:px-10 sm:pt-16">
        <div className="flex flex-col gap-4">
          <Badge className="w-fit border-white/10 bg-white/5 text-white/75">
            Docs
          </Badge>
          <h1 className="text-balance text-4xl font-semibold tracking-[-0.03em] text-white sm:text-6xl">
            Knowledge base.
          </h1>
          <p className="max-w-2xl text-[15px] leading-7 text-white/60">
            Explore how to define mocks in code, create Synthetic Environments,
            and integrate Smaughk into your development workflow. Our Interactive
            tool is under development.
          </p>
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl">
          <ResizablePanelGroup direction="horizontal" className="min-h-[520px]">
            {/* Sidebar */}
            <ResizablePanel defaultSize={30} minSize={22} maxSize={40}>
              <div className="flex h-full flex-col">
                <div className="border-b border-white/10 px-4 py-4">
                  <div className="flex items-center gap-2 text-[12px] font-medium text-white/70">
                    <BookOpen className="h-4 w-4" /> Browse
                  </div>
                  <div className="relative mt-3">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                    <Input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search docs…"
                      className="h-10 border-white/10 bg-white/[0.03] pl-10 text-white placeholder:text-white/35"
                    />
                  </div>
                </div>

                <ScrollArea className="h-full">
                  <div className="p-3">
                    {filtered.map((g) => (
                      <div key={g.group} className="mb-4">
                        <div className="px-2 py-2 text-[11px] font-medium uppercase tracking-[0.12em] text-white/35">
                          {g.group}
                        </div>
                        <div className="space-y-1">
                          {g.items.map((it) => {
                            const active = it.id === activeId;
                            return (
                              <button
                                key={it.id}
                                onClick={() => setActiveId(it.id)}
                                className={
                                  "w-full rounded-xl border px-3 py-2 text-left text-[13px] transition-colors " +
                                  (active
                                    ? "border-emerald-400/25 bg-emerald-400/10 text-white"
                                    : "border-white/10 bg-white/[0.02] text-white/70 hover:bg-white/[0.05]")
                                }
                                style={{ transition: "background-color 180ms ease, border-color 180ms ease" }}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <span className="truncate">{it.title}</span>
                                  <span className="text-[11px] text-white/35">
                                    {it.id}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    {query.trim() && (
                      <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-[12px] text-white/55">
                        Tip: try searching “config” or “edge”.
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            {/* Main split */}
            <ResizablePanel defaultSize={70} minSize={60}>
              <div className="grid h-full grid-rows-[auto_1fr]">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
                  <div>
                    <div className="text-[15px] font-medium text-white">
                      {active.title}
                    </div>
                    <div className="mt-1 text-[13px] text-white/55">
                      {activeId} • simulated playground
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => setConfig(mock.docs.starterConfig)}
                      className="h-9 rounded-xl border border-white/10 bg-white/5 px-3 text-white/80 hover:bg-white/10"
                    >
                      Reset config
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
                  <div className="border-b border-white/10 lg:border-b-0 lg:border-r">
                    <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-white/[0.02] px-4 py-3">
                      <div className="flex items-center gap-2 text-[12px] font-medium text-white/70">
                        <TerminalSquare className="h-4 w-4" /> smaughk.mocks.hcl
                      </div>
                      <div className="text-[12px] text-white/45">
                        editable
                      </div>
                    </div>
                    <textarea
                      value={config}
                      onChange={(e) => setConfig(e.target.value)}
                      spellCheck={false}
                      className="h-[320px] w-full resize-none bg-black/40 p-4 font-mono text-[12.5px] leading-6 text-white/80 outline-none"
                      style={{ transition: "background-color 180ms ease" }}
                    />
                    <div className="border-t border-white/10 px-4 py-3 text-[13px] leading-6 text-white/55">
                      {active.content}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-white/[0.02] px-4 py-3">
                      <div className="text-[12px] font-medium text-white/70">
                        Output
                      </div>
                      <div className="text-[12px] text-white/45">simulated</div>
                    </div>

                    <div className="p-4">
                      <Card className="rounded-2xl border-white/10 bg-white/[0.03] p-4">
                        <pre className="whitespace-pre-wrap font-mono text-[12.5px] leading-6 text-white/75">
                          {output.summary}
                        </pre>
                      </Card>

                      <div className="mt-4 grid gap-2">
                        {output.hints.map((h) => (
                          <div
                            key={h}
                            className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-[13px] text-white/60"
                          >
                            {h}
                          </div>
                        ))}
                      </div>

                      
                    </div>
                  </div>
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>

        <div className="mt-6 text-[12px] text-white/45">
          Tip: edit <span className="font-mono text-white/60">vus</span> or
          <span className="font-mono text-white/60"> edge</span> and watch the
          summary update.
        </div>
      </section>
    </div>
  );
}
